import type { Task } from '../../types';
import type { CommColumnId, CommTask } from './personModel';

// ───────────────────────────── Dates ─────────────────────────────

export function toISODate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function parseISODate(s?: string): Date | null {
  if (!s) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

export function daysBetween(fromISO: string, toISO: string): number {
  const a = parseISODate(fromISO);
  const b = parseISODate(toISO);
  if (!a || !b) return 0;
  return Math.round((b.getTime() - a.getTime()) / 86400000);
}

export function addDaysISO(baseISO: string, days: number): string {
  const d = parseISODate(baseISO) || new Date();
  d.setDate(d.getDate() + days);
  return toISODate(d);
}

/** Presets for quick deadline selection, matching Zeit-Kanban columns (Heute, Woche, Monat, Später, Keine). */
export function getDeadlinePresets(todayISO: string): { id: string; label: string; date: string | '' }[] {
  const today = parseISODate(todayISO) || new Date();
  const dayOfWeek = today.getDay(); // 0 = Sun, 1 = Mon ...
  // week: Mon-Thu -> Friday; Fri-Sun -> next Wednesday (guaranteed 1..7 days in future)
  const weekDays = (dayOfWeek >= 1 && dayOfWeek <= 4) ? (5 - dayOfWeek) : (dayOfWeek === 5 ? 5 : (dayOfWeek === 6 ? 4 : 3));
  const weekDate = addDaysISO(todayISO, weekDays);

  // month: end of month if >7 days, else today + 14 days
  const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  const endOfCurrMonth = toISODate(lastDay);
  const diff = daysBetween(todayISO, endOfCurrMonth);
  const monthDate = (diff > 7 && diff <= 30) ? endOfCurrMonth : addDaysISO(todayISO, 14);

  // longterm: today + 35 days (>30 days in future)
  const longtermDate = addDaysISO(todayISO, 35);

  return [
    { id: 'today', label: 'Heute', date: todayISO },
    { id: 'week', label: 'Woche', date: weekDate },
    { id: 'month', label: 'Monat', date: monthDate },
    { id: 'longterm', label: 'Später', date: longtermDate },
    { id: 'none', label: '–', date: '' }
  ];
}

const WEEKDAYS = ['So.', 'Mo.', 'Di.', 'Mi.', 'Do.', 'Fr.', 'Sa.'];

export function formatShortDate(iso: string, todayISO: string): string {
  const d = parseISODate(iso);
  if (!d) return '';
  const diff = daysBetween(todayISO, iso);
  if (diff === 0) return 'Heute';
  if (diff === 1) return 'Morgen';
  if (diff === -1) return 'Gestern';
  if (diff > 1 && diff < 7) return WEEKDAYS[d.getDay()];
  return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.`;
}

export type DeadlineTone = 'overdue' | 'today' | 'soon' | 'later' | 'followup-due' | 'followup';

export interface DeadlineInfo {
  label: string;
  tone: DeadlineTone;
  diffDays: number;
}

/** Phase-dependent deadline pill ("Erledigen bis" vs. "Nachfassen am"). */
export function getDeadlineInfo(t: Task, column: CommColumnId | null, todayISO: string): DeadlineInfo | null {
  if (!t.end || column === 'completed' || column === 'archived' || column === 'pool') return null;
  const diff = daysBetween(todayISO, t.end);
  if (column === 'pending') {
    if (diff <= 0) return { label: '↻ Nachfassen!', tone: 'followup-due', diffDays: diff };
    return { label: `↻ Nachfassen ${formatShortDate(t.end, todayISO)}`, tone: 'followup', diffDays: diff };
  }
  if (diff < 0) {
    const n = Math.abs(diff);
    return { label: `⚠ ${n} ${n === 1 ? 'Tag' : 'Tage'} überfällig`, tone: 'overdue', diffDays: diff };
  }
  if (diff === 0) return { label: 'Heute', tone: 'today', diffDays: diff };
  if (diff <= 2) return { label: formatShortDate(t.end, todayISO), tone: 'soon', diffDays: diff };
  return { label: formatShortDate(t.end, todayISO), tone: 'later', diffDays: diff };
}

export function getDeadlineLabelForColumn(column: CommColumnId | string | null | undefined): string {
  return column === 'pending' ? 'Nachfassen am' : 'Erledigen bis';
}

/** Days since the last contact (null if unknown). */
export function daysSinceContact(lastContactDate: string | undefined, todayISO: string): number | null {
  if (!lastContactDate) return null;
  return Math.max(0, daysBetween(lastContactDate.substring(0, 10), todayISO));
}

export function formatLastContact(days: number | null): string {
  if (days === null) return 'Noch kein Kontakt';
  if (days === 0) return 'Letzter Kontakt heute';
  if (days === 1) return 'Letzter Kontakt gestern';
  return `Letzter Kontakt vor ${days} Tagen`;
}

/** Sort key by urgency: overdue → today → later → none. */
export function compareByDeadline(a: Task, b: Task): number {
  if (a.end && b.end) return a.end.localeCompare(b.end);
  if (a.end) return -1;
  if (b.end) return 1;
  return (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
}

export function compareBySortOrder(a: Task, b: Task): number {
  const sA = a.sortOrder;
  const sB = b.sortOrder;
  if (sA !== undefined && sB !== undefined && sA !== sB) return sA - sB;
  if (sA !== undefined && sB === undefined) return -1;
  if (sB !== undefined && sA === undefined) return 1;
  return (a.createdAt ?? 0) - (b.createdAt ?? 0);
}

// ───────────────────────────── Stacking ─────────────────────────────

export type ColumnItem =
  | { kind: 'single'; id: string; task: CommTask; personKey?: string }
  | { kind: 'stack'; id: string; personKey: string; tasks: CommTask[] };

export const stackDndId = (column: string, personKey: string) => `s::${column}::${personKey}`;
export const vorgangDndId = (t: Task) => `v::${t.globalId || `${t.projectId}_${t.id}`}`;
export const personDndId = (personKey: string) => `p::${personKey}`;
export const avatarDndId = (personKey: string) => `pa::${personKey}`;

/**
 * Groups the (sortOrder-sorted) tasks of a column into single cards and person stacks.
 * - ≥2 Vorgänge of the same person ⇒ stack (positioned at its top-most member)
 * - inside a stack: sorted by deadline (overdue → today → later → none)
 * - stacking can be disabled (e.g. "Erledigt")
 */
export function groupColumnItems(
  tasks: CommTask[],
  column: string,
  personKeyOf: (t: Task) => string | undefined,
  enabled = true,
  unstackedTaskIds?: Set<string>
): ColumnItem[] {
  const sorted = [...tasks].sort(compareBySortOrder);
  if (!enabled) {
    return sorted.map(t => ({ kind: 'single' as const, id: vorgangDndId(t), task: t, personKey: personKeyOf(t) }));
  }

  const isTaskUnstacked = (t: CommTask) => Boolean(
    t.isInlineNew || t.unstacked || (unstackedTaskIds && (unstackedTaskIds.has(String(t.id)) || (t.globalId && unstackedTaskIds.has(t.globalId))))
  );

  const counts = new Map<string, number>();
  sorted.forEach(t => {
    if (isTaskUnstacked(t)) return;
    const k = personKeyOf(t);
    if (k) counts.set(k, (counts.get(k) || 0) + 1);
  });

  const items: ColumnItem[] = [];
  const stackByKey = new Map<string, Extract<ColumnItem, { kind: 'stack' }>>();
  sorted.forEach(t => {
    const k = personKeyOf(t);
    const canStack = k && !isTaskUnstacked(t) && (counts.get(k) || 0) >= 2;
    if (canStack) {
      let stack = stackByKey.get(k);
      if (!stack) {
        stack = { kind: 'stack', id: stackDndId(column, k), personKey: k, tasks: [] };
        stackByKey.set(k, stack);
        items.push(stack);
      }
      stack.tasks.push(t);
    } else {
      items.push({ kind: 'single', id: vorgangDndId(t), task: t, personKey: k });
    }
  });
  stackByKey.forEach(s => s.tasks.sort(compareByDeadline));
  return items;
}

/** Computes a sortOrder that places an item between two neighbours. */
export function sortOrderBetween(prev?: number, next?: number): number {
  if (prev === undefined && next === undefined) return 1000;
  if (prev === undefined) return (next as number) - 1000;
  if (next === undefined) return prev + 1000;
  return (prev + next) / 2;
}

/** Whether a completed Vorgang falls inside the visible "Erledigt" window. */
export function isRecentlyCompleted(t: Task, nowMs: number, windowDays = 14): boolean {
  const ts = t.completedAt || t.updatedAt || t.createdAt;
  if (!ts) return true;
  return nowMs - ts <= windowDays * 86400000;
}
